import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Box,
  Grid,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle,
  XCircle,
  BarChart2,
  RefreshCcw,
  Plus,
  AlertTriangle,
  Clock
} from 'lucide-react';
import TaskSettingsDialog from './TaskSettingsDialog';
import TaskListItem from './TaskListItem';
import api from './api';
import { toast } from 'react-toastify';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
  }
}));

const StyledCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5),
  paddingBottom: theme.spacing(0.5)
}));

const ScrollableTaskList = styled(Box)({
  maxHeight: '80vh',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px'
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#edbc4533',
    borderRadius: '4px',
    '&:hover': {
      background: '#edbc4566'
    }
  }
});

const WorkflowDashboard = () => {
  const history = useHistory();
  const [tasks, setTasks] = useState([]);
  const [isTaskSettingsOpen, setIsTaskSettingsOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastExecutedTask, setLastExecutedTask] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [taskStats, setTaskStats] = useState({
    workflowTasks: 0,
    codeTasks: 0,
    runningTasks: 0,
    completedTasks: 0,
    errorTasks: 0
  });

  // Handler to open workflow or code editor
  const handleOpenContent = (taskType, contentName) => {
    if (taskType === 'workflow') {
      // Navigate to workflow editor with workflow name
      history.push(`/flow/${encodeURIComponent(contentName)}`);
    } else if (taskType === 'code') {
      // Navigate to code editor with code name
      history.push(`/code-editor?name=${encodeURIComponent(contentName)}`);
    }
    // Close the settings dialog
    setIsTaskSettingsOpen(false);
    setSelectedTask(null);
  };

  const loadTasks = async () => {
    setIsLoading(true);
    try {
      const tasksResponse = await api.getSavedTasks();
      console.log('API Response:', tasksResponse);

      const formattedTasks = tasksResponse.map(task => {
        // Process date data
        let lastRun = 'Hiç';
        let executionTime = null;

        // Format date
        const formatDateTime = (dateString) => {
          if (!dateString) return '';

          try {
            // Split ISO string into parts
            const parts = dateString.split('T');
            if (parts.length !== 2) return '';

            const date = parts[0].split('-');
            const time = parts[1].split('.')[0];

            // Convert to dd.mm.yyyy hh:mm:ss format
            return `${date[2]}.${date[1]}.${date[0]} ${time}`;
          } catch (error) {
            console.error('Date formatting error:', error);
            return '';
          }
        };

        // Last execution time and duration
        if (task.lastStartTime && task.lastEndTime) {
          lastRun = formatDateTime(task.lastStartTime);

          // Calculate execution duration
          if (task.executionTime != null) {
            if (task.executionTime < 1) {
              executionTime = `${(task.executionTime * 1000).toFixed(0)}ms`;
            } else if (task.executionTime < 60) {
              executionTime = `${task.executionTime.toFixed(1)}s`;
            } else {
              const minutes = Math.floor(task.executionTime / 60);
              const seconds = (task.executionTime % 60).toFixed(0);
              executionTime = `${minutes}m ${seconds}s`;
            }
          }
        } else {
          lastRun = 'Hiç';
          executionTime = '-';
        }

        return {
          id: task.id,
          name: task.name,
          status: task.status || 'stopped',
          lastRun,
          executionTime: executionTime || '-',
          error: null,
          taskType: task.taskType || task.type || 'workflow',
          executionLocation: task.executionLocation || 'local',
          executionMode: task.executionMode || 'scheduler',
          errors: 0,
          successRate: 0,
          isEnabled: task.isEnabled ?? true,
          contentName: task.contentName || '',
          configuration: task.configuration,
          createdAt: formatDateTime(task.createdAt),
          updatedAt: formatDateTime(task.updatedAt),
          createdBy: task.createdBy,
          updatedBy: task.updatedBy,
          lastStartTime: task.lastStartTime,
          lastEndTime: task.lastEndTime
        };
      });

      console.log('Formatted tasks:', formattedTasks);
      setTasks(formattedTasks);

      // Calculate task statistics
      const stats = {
        workflowTasks: formattedTasks.filter(t => t.taskType === 'workflow').length,
        codeTasks: formattedTasks.filter(t => t.taskType === 'code').length,
        runningTasks: formattedTasks.filter(t => t.status === 'running').length,
        completedTasks: formattedTasks.filter(t => t.status === 'completed').length,
        errorTasks: formattedTasks.filter(t => t.status === 'error').length
      };
      setTaskStats(stats);

      // Find the last executed task (sorted by date)
      const sortedTasks = [...formattedTasks].sort((a, b) => {
        if (!a.lastStartTime) return 1;
        if (!b.lastStartTime) return -1;
        return new Date(b.lastStartTime) - new Date(a.lastStartTime);
      });

      if (sortedTasks.length > 0 && sortedTasks[0].lastStartTime) {
        setLastExecutedTask(sortedTasks[0]);
      }

      // Load last error
      loadLastError();

    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Görevler yüklenirken hata oluştu!');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLastError = async () => {
    try {
      const response = await api.getLastWorkflowError();
      if (response && response.errorDetails) {
        setLastError({
          message: response.errorDetails.Message,
          details: response.errorDetails.Details,
          taskName: response.taskName,
          taskId: response.taskId,
          startTime: response.startTime,
          endTime: response.endTime,
          status: response.status
        });
      }
    } catch (error) {
      console.error('Son hata yüklenirken hata oluştu:', error);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const handleNewTask = () => {
    setSelectedTask(null);
    setIsTaskSettingsOpen(true);
  };

  const handleEditTask = async (task) => {
    try {
        console.log('Editing task:', task);

        // Parse configuration
        let taskConfig = {};
        if (typeof task.configuration === 'string') {
            try {
                taskConfig = JSON.parse(task.configuration);
            } catch (e) {
                console.error('Error parsing configuration:', e);
            }
        } else {
            taskConfig = task.configuration || {};
        }

        // Map selected task data
        const mappedTask = {
            id: task.id,
            name: task.name,
            taskName: task.name,
            contentName: task.contentName || '',
            taskType: task.taskType || 'workflow',
            executionLocation: task.executionLocation || 'local',
            executionMode: task.executionMode || 'scheduler',
            isEnabled: task.isEnabled ?? true,
            configuration: taskConfig
        };

        console.log('Mapped task for editing:', mappedTask);

        setSelectedTask(mappedTask);
        setIsTaskSettingsOpen(true);

    } catch (error) {
        console.error('Görev düzenleme hazırlığında hata:', error);
        toast.error('Görev düzenlemek için hazırlanırken hata oluştu');
    }
  };

  const handleSaveTask = async (taskName, taskData) => {
    try {
      console.log("Saving task:", taskName, taskData);

      // Create or update task
      if (selectedTask) {
        await api.updateTask(taskName, taskData);
      } else {
        await api.createTask(taskData);
      }

      loadTasks(); // Reload tasks
      toast.success('Görev başarıyla kaydedildi');
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error('Görev kaydedilirken hata oluştu');
      throw error;
    }
  }

  const handleDeleteTask = async (taskName) => {
    try {
      setIsLoading(true);
      await api.deleteTask(taskName);

      setIsTaskSettingsOpen(false);
      toast.success('Görev başarıyla silindi!');

      setTimeout(() => {
        loadTasks();
      }, 500);

    } catch (error) {
      console.error('Görev silme hatası:', error);
      toast.error('Görev silinirken hata oluştu!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTask = async (task) => {
    try {
      if (task.status === 'running') {
        await api.stopTask(task.id);
        toast.success('Görev durduruldu');
      } else {
        await api.startTask(task.id);
        toast.success('Görev başlatıldı');
      }
      loadTasks();
    } catch (error) {
      toast.error('Görev durumu değiştirilirken hata oluştu!');
    }
  };

  // Format last task execution time
  const formatLastExecutionTime = () => {
    if (!lastExecutedTask || !lastExecutedTask.lastStartTime) {
      return 'Son zamanlarda çalıştırma yok';
    }

    const statusColor =
      lastExecutedTask.status === 'completed' ? '#4caf50' :
      lastExecutedTask.status === 'error' ? '#f44336' :
      lastExecutedTask.status === 'running' ? '#2196f3' : '#757575';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
          {lastExecutedTask.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="caption"
            component="span"
            sx={{
              color: statusColor,
              fontWeight: 'bold',
              bgcolor: `${statusColor}22`,
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: '0.7rem'
            }}
          >
            {lastExecutedTask.status}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {lastExecutedTask.lastRun}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, backgroundColor: '#fefaf4', minHeight: '100vh' }}>
      {/* Summary Cards */}
      <Grid container spacing={1} sx={{ mb: 2, maxWidth: '100%' }}>
        <Grid item xs={12} md={3.9} sx={{ maxWidth: '32%' }}>
          <StyledCard>
            <StyledCardHeader>
              <Typography variant="subtitle2" color="textSecondary">
                Aktif Görevler
              </Typography>
              <BarChart2 size={16} color="#6a1b9a" />
            </StyledCardHeader>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h5">
                {taskStats.workflowTasks} / {taskStats.codeTasks}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Workflow / Kod Görevleri
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium', color: '#6a1b9a' }}>
                {taskStats.runningTasks} görev çalışıyor
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={3.9} sx={{ maxWidth: '32%' }}>
          <StyledCard>
            <StyledCardHeader>
              <Typography variant="subtitle2" color="textSecondary">
                Son Çalıştırma
              </Typography>
              <Clock size={16} color="#2196f3" />
            </StyledCardHeader>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Yükleniyor...</Typography>
                </Box>
              ) : (
                formatLastExecutionTime()
              )}
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={3.9} sx={{ maxWidth: '32%' }}>
          <StyledCard>
            <StyledCardHeader>
              <Typography variant="subtitle2" color="textSecondary">
                Sistem Hataları
              </Typography>
              <XCircle size={16} color="#f44336" />
            </StyledCardHeader>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h5">
                {taskStats.errorTasks}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Hatalı görevler
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Task List */}
      <StyledCard sx={{ mb: 3 }}>
        <StyledCardHeader sx={{ backgroundColor: '#edbc45', color: '#000' }}>
          <Typography variant="h6" sx={{ color: '#000', fontWeight: 600 }}>
            Görevler
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={loadTasks}
              disabled={isLoading}
              startIcon={<RefreshCcw size={16} />}
              sx={{
                borderColor: '#000',
                color: '#000',
                '&:hover': { borderColor: '#333', backgroundColor: 'rgba(0,0,0,0.05)' }
              }}
            >
              Yenile
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleNewTask}
              startIcon={<Plus size={16} />}
              sx={{
                backgroundColor: '#000',
                color: '#edbc45',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#333' }
              }}
            >
              Yeni Görev
            </Button>
          </Box>
        </StyledCardHeader>
        <CardContent>
          <ScrollableTaskList>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#6a1b9a' }} />
              </Box>
            ) : tasks.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  Görev bulunamadı. Başlamak için yeni bir görev oluşturun.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {tasks.map(task => (
                  <TaskListItem
                    key={task.id}
                    task={task}
                    onToggle={handleToggleTask}
                    onEdit={handleEditTask}
                  />
                ))}
              </Box>
            )}
          </ScrollableTaskList>
        </CardContent>
      </StyledCard>

      {/* Last Error Section - Hidden */}
      {false && lastError && (
        <StyledCard>
          <StyledCardHeader>
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              Son Hata
            </Typography>
          </StyledCardHeader>
          <CardContent>
            <Alert
              severity="error"
              icon={<AlertTriangle size={16} />}
            >
              <AlertTitle>{lastError.message || 'Görev Çalıştırma Başarısız'}</AlertTitle>
              <Typography variant="body2">
                {lastError.details || 'Detaylı hata bilgisi mevcut değil'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
                Görev: {lastError.taskName || 'Bilinmeyen Görev'}
              </Typography>
              {lastError.endTime && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  {new Date(lastError.endTime).toLocaleString('tr-TR')}
                </Typography>
              )}
            </Alert>
          </CardContent>
        </StyledCard>
      )}

      {/* Task Settings Dialog */}
      <TaskSettingsDialog
        open={isTaskSettingsOpen}
        onClose={() => {
            setIsTaskSettingsOpen(false);
            setSelectedTask(null);
          }}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
        onOpenContent={handleOpenContent}
        initialSettings={selectedTask}
        isNew={!selectedTask}
      />
    </Box>
  );
};

export default WorkflowDashboard;
